import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { Button, Container, Header, PasswordField } from '@/components';
import { styled } from '@mui/material';
import { loginByInvitation } from '@/actions/authentication';
import { registrationByInvitation } from '@/actions/registration';
import t from '@/utils/translate';

import { Messages } from '@/components';

const Main = styled('main')(`
  position: fixed;
  top: 80px;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 24px;
`);

const Form = styled('form')(`
  margin: auto;
  width: 30%;
  min-width: 200px;
  max-width: 420px;
`);

export default () => {
  const { code } = useParams();
  const nav = useNavigate();
  const [needsRegistration, setNeedsRegistration] = useState(false);

  const formik = useFormik({
    initialValues: { password: '' },
    validateOnBlur: false,
    validateOnChange: false,
    validate: ({ password }) => {
      const errors: Record<string, string> = {};

      if (!password || password.length < 6) {
        errors.password = t('pwd.validation.pwdLen');
      } else if (!/\d/.test(password)) {
        errors.password = t('pwd.validation.pwdDig');
      } else if (!/[a-zа-я]/.test(password) || !/[A-ZА-Я]/.test(password)) {
        errors.password = t('pwd.validation.pwdReg');
      }

      return errors;
    },
    onSubmit: async ({ password }) => {
      try {
        formik.setSubmitting(true);
        await registrationByInvitation({ code: code!, password });
      } finally {
        formik.setSubmitting(false);
      }
      nav('/', { replace: true });
      nav(0);
    }
  });

  useEffect(() => {
    if (!code) {
      return;
    }
    if (!needsRegistration) {
      loginByInvitation(code).then((nr) => {
        if (nr) {
          setNeedsRegistration(nr);
          return;
        }
        nav('/', { replace: true });
        nav(0);
      }).catch(() => {
        nav('/', { replace: true });
        nav(0);
      });
    }
  }, [code, needsRegistration]);

  if (!code) {
    return (<Navigate to="/" replace/>);
  }

  if (!needsRegistration) {
    return <h3>{t('invitation.title.login')}</h3>
  }

  return (
    <>
      <Header/>

      <Main>
        <Form onSubmit={formik.handleSubmit}>
          <h3>{t('invitation.hint.pwd')}</h3>

          <Container margin-v='small'>
            <PasswordField
              {...formik.getFieldProps('password')}
              autoComplete="new-password"
              disabled={formik.isSubmitting}
              label={t('invitation.label.pwd')}
              error={formik.touched.password && !!formik.errors.password}
              helperText={formik.touched.password && formik.errors.password}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              autoFocus
            />
          </Container>
          <Button
            disabled={formik.isSubmitting}
            type="submit"
            variant="contained"
            fullWidth
            color="primary"
            size="large"
          >
            {t('invitation.action.register')}
          </Button>
        </Form>

        <footer>
          <Messages />
        </footer>
      </Main>
    </>
  );
};